import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Card,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  Select,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { WALLET_TRANSACTIONS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";
import UserDeposits from "./userDeposits";
import UserType from "./userType";
const WalletTransactions = () => {
  const [size, setSize] = useState("10");
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [editId, setEditId] = useState();
  const [openedDetail, setOpenedDetail] = useState(false);
  const [detailUserName, setDetailUserName] = useState(null);
  const [detailTotalAmount, setDetailTotalAmount] = useState(null);
  const [user, setUser] = useState(null);
  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const clearInput = () => {
    setConfirmedSearch("");
  };
  const { data, loading, fetchMore, refetch } = useQuery(WALLET_TRANSACTIONS, {
    variables: {
      first: Number.parseInt(size, 10),
      page: activePage,
      user_type: user,
      payment_method: paymentMethod,
      search: confirmedSearch,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get Categorys ",
      });
    },
  });

  const [fetchAllWalletTransactions] = useLazyQuery(WALLET_TRANSACTIONS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllWalletTransactions({
      variables: {
        first: null,
        page: 1,
        user_type: user,
        payment_method: paymentMethod,
        search: confirmedSearch,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
      },
    });
    return allData?.getWalletTransactions?.data || [];
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.getWalletTransactions.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, [refetch]);
  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: Number.parseInt(size, 10),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };
  if (!hasMounted) {
    return null;
  }

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx =
          (activePage - 1) * Number.parseInt(size || "0", 10) + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "user",
      sortable: false,
      width: "20%",

      searchable: false,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Email",
      key: "user",
      sortable: false,
      width: "20%",
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.email}</span>;
      },
    },
    {
      label: "Phone",
      key: "user",
      sortable: false,
      width: "20%",
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.phone}</span>;
      },
    },

    {
      label: "Amount",
      key: "amount",
      width: "10%",

      sortable: false,
      searchable: false,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_amount)}</span>;
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      exportable: false,
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleViewDetails(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const handleViewDetails = (rowData) => {
    setOpenedDetail(true);
    setEditId(rowData.transactions);
    setDetailUserName(rowData.name || null);
    setDetailTotalAmount(rowData.total_amount ?? null);
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedDetail}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpenedDetail(false)}
        position="bottom"
        size="80%"
      >
        <UserDeposits
          ordersList={editId}
          userName={detailUserName}
          totalAmount={detailTotalAmount}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            searchInput={false}
            activePage={activePage}
            filterData={({ onCardClick }) => (
              <div style={{ display: "flex", gap: "16px" }}>
                <UserType
                  setActivePage={setActivePage}
                  user={user}
                  onCardClick={setUser}
                />
                <Select
                  placeholder="Payment Method"
                  value={paymentMethod}
                  onChange={(value) => {
                    setPaymentMethod(value);
                    setActivePage(1);
                  }}
                  data={[
                    { value: "WALLET", label: "Wallet" },
                    { value: "TELEBIRR", label: "Tele birr" },
                    { value: "BANK_ADVICE", label: "Bank Advice" },
                  ]}
                  clearable
                  style={{ width: "250px" }}
                />
                <ExportComponent
                  data={data ? data.getWalletTransactions.data : []}
                  headers={headerData}
                  onFetch={handleExportDataFetch}
                  reportTitle="Wallet Transactions Report"
                  filterSubtitle={
                    `${user ? `User Type: ${user}` : "All Users"}` +
                    `${
                      paymentMethod ? `, Payment Method: ${paymentMethod}` : ""
                    }`
                  }
                  fileNamePrefix="wallet_transactions_report"
                />
              </div>
            )}
            clearInput={clearInput}
            handelSearch={setConfirmedSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            loading={loading}
            data={data ? data.getWalletTransactions.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default WalletTransactions;
